import { Temporal } from "@js-temporal/polyfill";
import { PLANNING_TIMEZONE } from "./constants.js";
/**
 * Matches RFC 3339 datetime with an explicit offset:
 *   2026-01-15T08:00:00+01:00
 *   2026-01-15T07:00:00Z
 *   2026-03-29T02:30:00.123+02:00
 */
const RFC3339_WITH_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
/**
 * Predicate: is `input` a valid RFC 3339 datetime with an explicit offset?
 * Checks both the syntactic shape and calendar validity (e.g. rejects
 * `"2025-02-30T10:30:00Z"`), so a passing input never makes the converters
 * below throw.
 */
export function isRfc3339WithOffset(input) {
    if (!RFC3339_WITH_OFFSET.test(input))
        return false;
    try {
        Temporal.Instant.from(input);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Convert any valid RFC 3339 string to a JS `Date`; throws on invalid input.
 */
export function rfc3339ToDate(input) {
    if (!isRfc3339WithOffset(input)) {
        throw new Error(`Expected RFC 3339 datetime with offset, got: "${input}"`);
    }
    const instant = Temporal.Instant.from(input);
    return new Date(instant.epochMilliseconds);
}
/**
 * Predicate: does the offset in `input` match what Europe/Berlin would have at
 * that instant?  Assumes `input` already passes `isRfc3339WithOffset`.
 */
export function hasBerlinOffset(input) {
    const instant = Temporal.Instant.from(input);
    const berlinZdt = instant.toZonedDateTimeISO(PLANNING_TIMEZONE);
    const inputOffsetMin = extractOffsetMinutes(input);
    const berlinOffsetMin = Math.round(berlinZdt.offsetNanoseconds / 60_000_000_000);
    return inputOffsetMin === berlinOffsetMin;
}
/**
 * Convert an RFC 3339 string to a JS `Date`, asserting the offset matches what
 * Europe/Berlin would have at that instant; throws on mismatch.
 */
export function berlinRfc3339ToDate(input) {
    if (!isRfc3339WithOffset(input)) {
        throw new Error(`Expected RFC 3339 datetime with offset, got: "${input}"`);
    }
    const instant = Temporal.Instant.from(input);
    const berlinZdt = instant.toZonedDateTimeISO(PLANNING_TIMEZONE);
    const inputOffsetMin = extractOffsetMinutes(input);
    const berlinOffsetMin = Math.round(berlinZdt.offsetNanoseconds / 60_000_000_000);
    if (inputOffsetMin !== berlinOffsetMin) {
        throw new Error(`Planned datetime offset does not match Europe/Berlin (expected ${berlinZdt.offset}): "${input}"`);
    }
    return new Date(instant.epochMilliseconds);
}
function extractOffsetMinutes(rfc3339) {
    if (rfc3339.endsWith("Z"))
        return 0;
    const match = /([+-])(\d{2}):(\d{2})$/.exec(rfc3339);
    if (!match)
        return NaN;
    const sign = match[1] === "+" ? 1 : -1;
    return sign * (Number(match[2]) * 60 + Number(match[3]));
}
